import { ICombinedPreferences } from "@interfaces/ICombinedPreferences";
import { IJourney } from "@interfaces/IJourney";
import { IUser } from "@interfaces/IUser";
import { IVehicle } from "@interfaces/IVehicle";
import { ChartDefinition } from "@reports/enums/ChartDefinition";
import { CurrentUserService } from "@services/currentUser.service";
import { Debounce } from "@services/debounce.service";
import { JourneyService } from "@services/journey.service";
import { PreferencesService } from "@services/preferences.service";
import { VehicleService } from "@services/vehicle.service";
import * as angular from "angular";
import { IOnChangesObject } from "angular";
import copy from "fast-copy/dist/fast-copy.esm.js";
import * as moment from "moment";
import { DynamicReportService } from "../../_services/dynamicReports.service";
import { ReportTableRowDataSource } from "../../enums/ReportTableRowDataSource";
import { IColumnHeader } from "../../models/IColumnHeader";
import { IHtmlDocument } from "../../models/IHtmlDocument";
import { IDataDefinition, IReportDefinition } from "../../models/IReportDefinition";
import "./reportBuilder.scss";

export interface IReportBuilderComponentBindings {
    reportDefinition: IReportDefinition;

    onSave: (args: { $report: IReportDefinition }) => void;
    onClose: () => void;
    onDelete: () => void;
}

class ReportBuilderComponentController implements angular.IComponentController, IReportBuilderComponentBindings {
    currentUser: IUser;
    combinedPreferences: ICombinedPreferences;
    reportDefinition: IReportDefinition;
    reportDefinitions: IReportDefinition[];

    reportTypeHeaders: IColumnHeader[] = [];

    onSave: (args: { $report: IReportDefinition }) => void;
    onClose: () => void;
    onDelete: () => void;

    enableTabPreview = false;
    enableSaveReport = false;

    loadingPreview: boolean;
    htmlDocument: IHtmlDocument;

    includeRecordsWithNoData = false;

    rowTitleHeader: IColumnHeader;
    sortAscending: boolean;
    sortHeaderId: number;

    previewReportDebounce: Function;

    vehicleWithMostJourneys: number;

    showTemperatureChartCheckBox: boolean;
    showTemperatureCharts: boolean;
    showChangeParameters: boolean;

    static $inject = ["$q", "dynamicReportService", "debounceService", "journeyService", "vehicleService", "preferencesService"];

    constructor(
        private readonly $q: angular.IQService,
        private readonly reportService: DynamicReportService,
        private readonly debounceService: Debounce,
        private readonly journeyService: JourneyService,
        private readonly vehicleService: VehicleService,
        private readonly preferencesService: PreferencesService,
        private readonly currentUserService: CurrentUserService
    ) {
        this.currentUserService.getCurrentUser().then(user => {
            this.currentUser = user;
        });
    }

    $onInit(): void {
        this.loadData();
    }

    $onChanges(onChangesObj: IOnChangesObject): void {
        if (onChangesObj["reportDefinition"] && this.reportDefinition) {
            this.reportDefinition = copy(this.reportDefinition);
            this.checkForTemperatureChartColumns();

            if (this.reportDefinition.ShowCharts &&
                this.reportDefinition.ChartDefinition === ChartDefinition.Temperature) {
                this.showTemperatureCharts = true;
            }

            this.getDataSourceDefinitionsAndMapToColumns(this.reportDefinition.TableDefinition.Headers);
            this.validateForm();
        }
    }

    private checkForTemperatureChartColumns() {
        this.showTemperatureChartCheckBox = this.reportService.findColumn("ProbeTemperatures", "Temperature", this.reportDefinition.TableDefinition.Headers) != null;

        if (!this.showTemperatureChartCheckBox) {
            this.showTemperatureCharts = false;
        }
    }

    onBackClicked() {
        if (this.onClose) {
            this.onClose();
        }
    }

    rowDataSourceChanged($row: ReportTableRowDataSource, $subRow: ReportTableRowDataSource) {
        this.reportDefinition.RowDataSource = $row;
        this.reportDefinition.SubRowDataSource = $subRow;

        this.setRowTitle();

        this.headerSortChanged();

        this.validateForm();
        this.loadPreview();

        this.updateSelectedHeaderSortStatus();
        this.reportDefinition.TableDefinition.Headers = copy(this.reportDefinition.TableDefinition.Headers);
    }

    headerSortChanged() {
        this.sortHeaderId = Number(this.sortHeaderId);

        this.updateSortedColumn();
        this.loadPreview();
    }

    validateForm() {
        this.enableTabPreview = false;
        this.enableSaveReport = false;

        if (!this.reportDefinition.TableDefinition.Headers || this.reportDefinition.TableDefinition.Headers.length <= 1) {
            return;
        }

        this.enableTabPreview = true;

        if (!this.reportDefinition || !this.reportDefinition.Title || !this.reportDefinition.RowDataSource) {

            return;
        }

        this.enableSaveReport = true;
    }

    deleteDefinedReport() {
        this.reportDefinition.Active = false;

        this.reportService.getReportSchedules({ ReportDefinitionId: this.reportDefinition.Id }).then(schedules => {
            schedules.forEach(schedule => {
                schedule.Active = false;
                this.reportService.saveSchedule(schedule);
            });
        });

        this.reportService.deleteReportDefinition(this.reportDefinition.Id).then(() => {
            if (this.onDelete) {
                this.onDelete();
            }
        });
    }

    saveDefinedReport(copy: boolean) {
        if (!this.reportDefinition) {
            this.reportDefinition = {
                TableDefinition: {
                    Headers: [],
                    Rows: []
                },
                RowDataSource: ReportTableRowDataSource.Vehicle,
                SubRowDataSource: ReportTableRowDataSource.None
            };
        }

        if (copy) {
            this.reportDefinition.newReport = true;
            this.reportDefinition.Title += this.reportTileInUse(this.reportDefinition.Title) ? " (Copy)" : "";
            this.reportDefinition.Id = 0;
        }

        this.reportDefinition.isSystemReport = false;

        this.reportService.saveReportDefinition(this.reportDefinition).then(result => {
            if (this.onSave) {
                this.onSave({ $report: result });
            }
        });
    }

    selectedHeadersChanged(columns: IColumnHeader[]) {
        this.reportDefinition.TableDefinition.Headers = copy(columns);

        this.checkForTemperatureChartColumns();

        this.ensureUniqueColumnIds();

        this.validateForm();
        this.loadPreview();

        this.updateSortedColumn();
        this.updateSelectedHeaderSortStatus();
    }

    updateSortedColumn() {
        for (let i = 0; i < this.reportDefinition.TableDefinition.Headers.length; i++) {
            const column = this.reportDefinition.TableDefinition.Headers[i];
            column.Sorted = false;

            if (column.Id === this.sortHeaderId) {
                column.Sorted = true;
                column.SortAscending = this.sortAscending;
            }
        }
    }

    orientationChanged() {
        this.reportDefinition.Portrait = this.reportDefinition.Portrait.toString() == "true";
        this.previewReportDebounce();
    }

    changeParameters() {
        this.showChangeParameters = !this.showChangeParameters;
    }

    // --------------------

    private loadData() {

        this.loadingPreview = true;

        const vehiclesPromise = this.vehicleService.getVehicles({ includePeripheralMappings: true });
        const journeysPromise = this.journeyService.getAllJourneys(moment().startOf("day").toDate(), moment().endOf("day").toDate());
        const preferencesPromise = this.preferencesService.getAllPreferences();
        const columnHeaderPromise = this.getAndSetColumnHeaders();
        const definitionPromise = this.reportService.getReports();

        this.$q
            .all([vehiclesPromise, journeysPromise, preferencesPromise, definitionPromise, columnHeaderPromise])
            .then(([vehicles, journeys, combinedPreferences, definitions]) => {
                if (!this.reportDefinition) {
                    this.createDefaultDefinition();
                }

                this.findAndSetVehicleWithMostJourneys(vehicles, journeys);

                this.combinedPreferences = combinedPreferences;
                this.reportDefinitions = definitions;

                this.selectReport(this.reportDefinition);
            })
            .finally(() => {
                this.loadingPreview = false;
            });

        this.createPreviewDebounceMethod();
    }

    private createPreviewDebounceMethod() {

        this.previewReportDebounce = this.debounceService.debounce(
            () => {
                if (this.reportDefinition.TableDefinition.Headers.length <= 1) {
                    this.htmlDocument = null;
                    return;
                }

                this.loadingPreview = true;

                this.reportDefinition.Parameters = {
                    Start: moment().startOf("day").toDate(),
                    End: moment().endOf("day").toDate()
                };

                this.reportDefinition.ShowCharts = false;
                if (this.showTemperatureCharts) {
                    this.reportDefinition.ChartDefinition = ChartDefinition.Temperature;
                    this.reportDefinition.ShowCharts = true;
                }

                if (this.vehicleWithMostJourneys && this.reportService.restrictReportToSingleResource(this.reportDefinition)) {
                    this.reportDefinition.Parameters.VehicleId = this.vehicleWithMostJourneys;
                }

                if (this.reportDefinition && this.combinedPreferences) {
                    this.reportDefinition.DistanceMeasurement = this.combinedPreferences.DistanceMeasurement.Value;
                    this.reportDefinition.VolumeMeasurement = this.combinedPreferences.VolumeMeasurement.Value;
                    this.reportDefinition.TemperatureMeasurement = this.combinedPreferences.TemperatureMeasurement.Value;
                    this.reportDefinition.Currency = this.combinedPreferences.Currency.Value;

                    this.reportService.previewReport(this.reportDefinition)
                        .then(document => {
                            this.htmlDocument = document;
                            this.loadingPreview = false;
                        });
                }
            },
            500,
            false);
    }

    report: IDataDefinition;

    private findAndSetVehicleWithMostJourneys(vehicles: IVehicle[], journeys: IJourney[]) {
        if (!journeys || journeys.length == 0) {
            return;
        }

        var currentMaxCount = 0;

        vehicles.forEach(vehicle => {
            vehicle.rangeJourneyCount = 0;

            journeys.forEach(journey => {
                if (journey.VehicleId === vehicle.Id) {
                    vehicle.rangeJourneyCount++;
                }
            });

            if (vehicle.rangeJourneyCount > currentMaxCount) {
                this.vehicleWithMostJourneys = vehicle.Id;
            }
        });
    }

    private reportTileInUse(title: string): boolean {
        var isInUse = false;

        this.reportDefinitions.forEach(report => {
            if (title === report.Title) {
                isInUse = true;
            }
        });

        return isInUse;
    }

    private selectReport(reportDef: IReportDefinition) {
        this.reportDefinition = reportDef;

        this.ensureUniqueColumnIds();
        this.setRowTitle();
        this.mapColumnDefinitions();
        this.validateForm();

        this.updateSelectedHeaderSortStatus();

        this.previewReportDebounce();
    }

    private getAndSetColumnHeaders(): angular.IPromise<any> {
        return this.reportService.getDisplayColumnHeaders().then(columns => {

            var displayColumns: IColumnHeader[] = [];
            columns.forEach(column => {
                if (column.dataSourceDefinition && column.dataSourceDefinition.IsVisible && column.ColumnDefinition && column.ColumnDefinition.Format && column.ColumnDefinition.Format.IsVisible) {
                    displayColumns.push(column);
                }
            });

            this.reportTypeHeaders = displayColumns;
            this.rowTitleHeader = this.reportService.findRowTitleColumn(columns);
        });
    }

    private ensureUniqueColumnIds() {
        this.reportDefinition.TableDefinition.Headers.forEach(column => {
            this.ensureUniqueColumnId(column);
        });
    }

    private ensureUniqueColumnId(column: IColumnHeader) {
        if (!column.Id || isNaN(column.Id)) {
            column.Id = Math.floor(Math.random() * 20000000);
        }
    }

    private createDefaultDefinition() {
        this.reportDefinition = {
            Active: true,
            Title: null,
            RowDataSource: ReportTableRowDataSource.Vehicle,
            SubRowDataSource: ReportTableRowDataSource.None,
            Portrait: true,
            TableDefinition: {
                Headers: [],
                Rows: [],
                SummarySections: []
            },
            newReport: true,
            Shared: false
        };

        this.selectReport(this.reportDefinition);

        this.sortAscending = true;
    }

    private getDataSourceDefinitionsAndMapToColumns(columns: IColumnHeader[]) {
        return this.reportService
            .getDataSourceDefinitions()
            .then(dataSourceDefinition => {
                this.reportService.mapDataSourceDefinitionsToColumns(columns, dataSourceDefinition);
                this.reportService.formatColumnsForDisplay(columns);

                this.updateSelectedHeaderSortStatus();
            });
    }

    private updateSelectedHeaderSortStatus() {
        this.sortAscending = true;
        this.sortHeaderId = -1;

        this.reportDefinition.TableDefinition.Headers.forEach(header => {
            if (header.Sorted) {
                this.sortHeaderId = header.Id;
                this.sortAscending = header.SortAscending;
            }
        });

        if (this.sortHeaderId < 1 && this.reportDefinition.TableDefinition.Headers.length > 0) {
            this.sortHeaderId = this.reportService.findRowTitleColumn(this.reportDefinition.TableDefinition.Headers).Id;
        }
    }

    private loadPreview() {
        if (!this.enableTabPreview || !this.previewReportDebounce) {
            this.htmlDocument = null;
            return;
        }

        this.previewReportDebounce();
    }

    private setRowTitle() {
        const newHeaderColumn: IColumnHeader = copy(this.rowTitleHeader);

        newHeaderColumn.Title = this.getTitleForRowDataSource();
        newHeaderColumn.IsRowTitle = true;
        newHeaderColumn.Sorted = true;

        this.ensureUniqueColumnId(newHeaderColumn);
        this.findAndReplaceRowTitle(newHeaderColumn);
    }

    private findAndReplaceRowTitle(titleHeader: IColumnHeader) {
        const index = this.reportDefinition.TableDefinition.Headers.findIndex(header => header.IsRowTitle || header.DataSourceValue === "RowTitle");

        if (index) {
            this.reportDefinition.TableDefinition.Headers.splice(index, 1, titleHeader);
            return;
        }

        this.reportDefinition.TableDefinition.Headers.splice(0, 1, titleHeader);
    }

    private mapColumnDefinitions() {
        if (!this.reportDefinition) {
            return;
        }

        this.reportDefinition.TableDefinition.Headers.forEach(header => {
            this.reportTypeHeaders.forEach(typeHeader => {
                if (typeHeader.DataSourceValue === header.DataSourceValue) {
                    header.ColumnDefinition = typeHeader.ColumnDefinition;
                }
            });
        });
    }

    private getTitleForRowDataSource(): string {
        let dataSource = this.reportDefinition.RowDataSource;

        if (Number.isNaN(dataSource) || typeof (dataSource) === "string") {
            dataSource = Number.parseInt(this.reportDefinition.RowDataSource.toString());
        }

        switch (dataSource) {
            case ReportTableRowDataSource.Day:
                return "Day";
            case ReportTableRowDataSource.Week:
                return "Week";
            case ReportTableRowDataSource.Driver:
                return "Driver";
            case ReportTableRowDataSource.Vehicle:
                return "Vehicle";
            case ReportTableRowDataSource.DriverGroup:
                return "Driver Group";
            case ReportTableRowDataSource.VehicleGroup:
                return "Vehicle Group";
            case ReportTableRowDataSource.Geofence:
                return "Geofence";
            case ReportTableRowDataSource.Journey:
                return "Journey";
            case ReportTableRowDataSource.Route:
                return "Route";
            case ReportTableRowDataSource.StationaryLocations:
                return "Stationary Location";
            case ReportTableRowDataSource.TemperatureRecord:
                return "Temperature Record";
            case ReportTableRowDataSource.TrailerAssociation:
                return "Trailer Association";
        }

        return ReportTableRowDataSource[dataSource];
    }
}

export class ReportBuilderComponent implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    template: string;

    constructor() {
        this.bindings = {
            reportDefinition: "<",

            onSave: "&",
            onDelete: "&",
            onClose: "&"
        };

        this.controller = ReportBuilderComponentController;
        this.template = require("./reportBuilder.html");
    }
}
